/* Grid layout per user (pure helpers): which grid a layout belongs to and how a saved layout changes the grid's columns.
 * A layout keeps only what the user changed: the column order, widths and shown / hidden columns - never filters, search,
 * sort or paging. The grid key is the page plus the grid's columns. When a release adds or removes columns, the user's layout
 * of that page whose columns overlap most (at least half) is carried over: kept columns keep their order / width / shown,
 * a new column is shown after the column it follows in the code, a removed one is dropped.
 * Saving / loading and the hook into every DataGrid: gridLayoutGlobal.js. */

/** Page path without ids, so /driver/123 and /driver/456 share one layout. */
export function normalizePath(path) {
  return (path || '/').split('/').map((p) => (/^\d+$/.test(p) || /^[0-9a-f-]{32,36}$/i.test(p) ? ':id' : p)).join('/').toLowerCase();
}

/** Name of a column in a layout: dataField, name or caption (column position when it has none); duplicates get ~2, ~3. */
export function columnIds(columns) {
  const seen = {};
  return (columns || []).map((c, i) => {
    const base = typeof c === 'string' ? c : c?.dataField || c?.name || c?.caption || `#${i}`;
    seen[base] = (seen[base] || 0) + 1;
    return seen[base] > 1 ? `${base}~${seen[base]}` : base;
  });
}

/** Column identifier the grid accepts in columnOption(), or null for a column without dataField / name / caption. */
export function columnRef(column) {
  if (typeof column === 'string') return column;
  return column?.dataField || column?.name || column?.caption || null;
}

function hash(text) {
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) h = ((h * 33) ^ text.charCodeAt(i)) >>> 0; // eslint-disable-line no-bitwise
  return h.toString(36);
}

/** Key of a grid: page + a short hash of its columns (max 400 chars). */
export function gridKey(path, ids) {
  return `${normalizePath(path).slice(0, 380)}#${hash(ids.join('|'))}`;
}

export const emptyLayout = () => ({ v: 1, ids: null, order: null, widths: {}, hidden: {} });

/** Reads a stored layout (JSON text or object); null when it is not one. */
export function parseLayout(value) {
  try {
    const l = typeof value === 'string' ? JSON.parse(value) : value;
    if (!l || l.v !== 1) return null;
    return {
      v: 1, ids: Array.isArray(l.ids) ? l.ids : null, order: Array.isArray(l.order) ? l.order : null, widths: l.widths || {}, hidden: l.hidden || {},
      ...(l.reset ? { reset: true } : {}),
    };
  } catch {
    return null;
  }
}

export const isEmptyLayout = (l) => !l || (!l.order && !Object.keys(l.widths).length && !Object.keys(l.hidden).length);

/** Per column what a layout sets: { visibleIndex?, width?, visible? } (only the parts the user changed). */
export function layoutSettings(ids, layout) {
  const order = layout?.order;
  const pos = {};
  if (order) order.forEach((id, i) => { pos[id] = i; });
  return ids.map((id, i) => {
    const s = {};
    if (order) s.visibleIndex = id in pos ? pos[id] : order.length + i;
    const w = layout?.widths?.[id];
    if (typeof w === 'number' || (typeof w === 'string' && w)) s.width = w;
    if (layout?.hidden && id in layout.hidden) s.visible = !layout.hidden[id];
    return s;
  });
}

/** The grid's column configuration with a saved layout applied (a new array, the original is not changed). */
export function applyLayout(columns, ids, layout) {
  const settings = layoutSettings(ids, layout);
  return columns.map((c, i) => ({ ...(typeof c === 'string' ? { dataField: c } : c), ...settings[i] }));
}

const pick = (obj, ids) => Object.fromEntries(Object.entries(obj || {}).filter(([k]) => ids.includes(k)));

/** A saved layout for the grid's current columns: removed columns dropped, a new column placed after the column it follows
 * in the code (first when none of the earlier ones is in the order). */
export function carryLayout(saved, ids) {
  let order = null;
  if (saved.order) {
    order = saved.order.filter((id) => ids.includes(id));
    ids.forEach((id, i) => {
      if (order.includes(id)) return;
      let at = 0;
      for (let j = i - 1; j >= 0; j -= 1) {
        const p = order.indexOf(ids[j]);
        if (p >= 0) { at = p + 1; break; }
      }
      order.splice(at, 0, id);
    });
  }
  return { v: 1, ids: [...ids], order, widths: pick(saved.widths, ids), hidden: pick(saved.hidden, ids) };
}

/** Share of columns two grids have in common (0..1). */
export function overlap(a, b) {
  if (!a?.length || !b?.length) return 0;
  const shared = a.filter((id) => b.includes(id)).length;
  return shared / Math.max(a.length, b.length);
}

/** Layout saved by Reset: the grid shows its default columns and nothing is carried over to it. */
export const resetMarker = (ids) => ({ ...emptyLayout(), ids: [...ids], reset: true });

/** The user's layout for a grid: its own, or else a copy of the best overlapping one (>= 50%) of the same page, carried
 * over (the other grid keeps its own). Null for none or after Reset. */
export function findLayout(layouts, key, ids) {
  const own = layouts.get(key);
  if (own) return own.reset ? null : carryLayout(own, ids);
  const page = key.slice(0, key.lastIndexOf('#') + 1);
  let best = null;
  layouts.forEach((l, k) => {
    if (k === key || !k.startsWith(page) || k.lastIndexOf('#') + 1 !== page.length || !l.ids || l.reset) return;
    const score = overlap(l.ids, ids);
    if (score >= 0.5 && (!best || score > best.score)) best = { score, k, l };
  });
  return best ? carryLayout(best.l, ids) : null;
}

/** Order of the columns (their names) from each column's visibleIndex; ties keep the code order. */
export function orderFrom(ids, visibleIndexes) {
  return ids.map((id, i) => ({ id, i, vi: Number.isFinite(visibleIndexes[i]) ? visibleIndexes[i] : 1e6 + i }))
    .sort((a, b) => a.vi - b.vi || a.i - b.i).map((x) => x.id);
}
